import React, { useState, useEffect } from 'react';
import { Bell, BellOff, X, Smartphone, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { toast } from 'sonner';

const STORAGE_KEY = 'push_prompt_dismissed_v7'; // v7: Nach Deploy reset → zeigt für alle wieder
const DISMISS_TTL_MS = 14 * 24 * 60 * 60 * 1000; // 14 Tage

export const PushNotificationPrompt = () => {
  const { permission, isSupported, isSubscribed, loading, subscribe } = usePushNotifications();
  const [visible, setVisible] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [subscribeSuccess, setSubscribeSuccess] = useState(false);

  // iOS erkennen
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true;

  useEffect(() => {
    if (!isSupported) return;
    if (isSubscribed) return;
    if (permission === 'denied') return;

    const dismissedAt = parseInt(localStorage.getItem(STORAGE_KEY) || '0', 10);
    const isExpired = Date.now() - dismissedAt > DISMISS_TTL_MS;
    if (dismissedAt && !isExpired) return; // Innerhalb von 14 Tagen nach Dismiss → nicht zeigen

    if (dismissedAt && isExpired) {
      localStorage.removeItem(STORAGE_KEY); // Abgelaufen → zurücksetzen
    }

    const timer = setTimeout(() => setVisible(true), 3000);
    return () => clearTimeout(timer);
  }, [isSupported, isSubscribed, permission]);

  const handleDismiss = () => {
    setVisible(false);
    localStorage.setItem(STORAGE_KEY, String(Date.now())); // Zeitstempel speichern (läuft nach 14 Tagen ab)
  };

  const handleEnable = async () => {
    // iOS: Erst prüfen ob als PWA installiert
    if (isIOS && !isInStandaloneMode) {
      setShowIOSGuide(true);
      return;
    }

    const result = await subscribe();
    if (result.success) {
      setSubscribeSuccess(true);
      toast.success('Push-Benachrichtigungen aktiviert!');
      setTimeout(() => { setVisible(false); }, 2000);
    } else if (result.reason === 'denied') {
      toast.error('Benachrichtigungen wurden abgelehnt. Bitte in den Browser-Einstellungen aktivieren.');
      handleDismiss();
    } else if (result.reason === 'error') {
      toast.error(`Fehler: ${result.error || 'Push-Aktivierung fehlgeschlagen. Bitte erneut versuchen.'}`);
      // Prompt offen lassen damit User es nochmal versuchen kann
    }
  };

  if (!visible) return null;

  // iOS-Anleitung
  if (showIOSGuide) {
    return (
      <div className="fixed bottom-4 left-4 right-4 z-50 sm:left-auto sm:right-4 sm:w-80">
        <div className="bg-card border border-border rounded-2xl shadow-2xl p-4 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <Smartphone className="h-5 w-5 text-primary shrink-0" />
              <p className="font-semibold text-sm text-foreground">iOS: App zum Home-Bildschirm hinzufügen</p>
            </div>
            <button onClick={() => setShowIOSGuide(false)} className="text-muted-foreground hover:text-foreground shrink-0">
              <X className="h-4 w-4" />
            </button>
          </div>

          <ol className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs shrink-0 mt-0.5">1</span>
              <span>Tippe unten auf das <strong className="text-foreground">Teilen-Symbol</strong> (Kasten mit Pfeil nach oben)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs shrink-0 mt-0.5">2</span>
              <span>Wähle <strong className="text-foreground">"Zum Home-Bildschirm"</strong></span>
            </li>
            <li className="flex items-start gap-2">
              <span className="bg-primary text-primary-foreground rounded-full w-5 h-5 flex items-center justify-center text-xs shrink-0 mt-0.5">3</span>
              <span>Öffne die App über das neue Icon und aktiviere Benachrichtigungen</span>
            </li>
          </ol>

          <Button variant="outline" size="sm" onClick={handleDismiss} className="w-full">
            Verstanden
          </Button>
        </div>
      </div>
    );
  }

  // Erfolg
  if (subscribeSuccess) {
    return (
      <div className="fixed bottom-4 right-4 z-50 w-72">
        <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-2xl shadow-xl p-4 flex items-center gap-3">
          <CheckCircle2 className="h-6 w-6 text-green-500 shrink-0" />
          <div>
            <p className="font-semibold text-sm text-green-800 dark:text-green-300">Aktiviert!</p>
            <p className="text-xs text-green-600 dark:text-green-400">Du erhältst jetzt Push-Benachrichtigungen</p>
          </div>
        </div>
      </div>
    );
  }

  // Haupt-Banner
  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 sm:left-auto sm:right-4 sm:w-80" data-testid="push-prompt">
      <div className="bg-card border border-border rounded-2xl shadow-2xl p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <div className="bg-primary/10 rounded-xl p-2">
              <Bell className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="font-semibold text-sm text-foreground">Benachrichtigungen</p>
              <p className="text-xs text-muted-foreground">Bleib immer auf dem aktuellen Stand</p>
            </div>
          </div>
          <button onClick={handleDismiss} className="text-muted-foreground hover:text-foreground" data-testid="push-prompt-dismiss">
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="text-xs text-muted-foreground">
          Erhalte sofortige Benachrichtigungen für Unterweisungen, Aufgaben und wichtige Updates – auch wenn die App geschlossen ist.
        </p>

        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleDismiss}
            className="flex-1 text-xs"
          >
            Nicht jetzt
          </Button>
          <Button
            size="sm"
            onClick={handleEnable}
            disabled={loading}
            className="flex-1 text-xs gap-1.5"
            data-testid="push-enable-btn"
          >
            <Bell className="h-3.5 w-3.5" />
            {loading ? 'Aktiviere...' : 'Aktivieren'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PushNotificationPrompt;
